// Store and retrieve ONLY tenant_id and tenant config from session
// NEVER store service role keys in session/cookies/JWT

const TENANT_ID_KEY = 'tenant_id';
const TENANT_CONFIG_KEY = 'tenant_config';
/** Persisted salon name for sidebar / instant UI; kept in sync with tenant_config on write. */
const SALON_DISPLAY_NAME_KEY = 'tenant_salon_display_name';
const SALON_DISPLAY_UPDATED_AT_KEY = 'tenant_salon_display_name_updated_at';

/** Same-tab listeners (storage event only fires across tabs). */
export const TENANT_SALON_DISPLAY_CHANGE_EVENT = 'pos-tenant-salon-display-changed';

function notifySalonDisplaySubscribers(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(TENANT_SALON_DISPLAY_CHANGE_EVENT));
}

function setSalonDisplayCache(displayName: string): void {
  if (typeof window === 'undefined') return;
  const trimmed = displayName.trim();
  if (!trimmed) return;
  localStorage.setItem(SALON_DISPLAY_NAME_KEY, trimmed);
  localStorage.setItem(SALON_DISPLAY_UPDATED_AT_KEY, String(Date.now()));
}

function writeSalonDisplayCache(displayName: string): void {
  setSalonDisplayCache(displayName);
  notifySalonDisplaySubscribers();
}

export interface StoredTenantConfig {
  supabase_url: string;
  supabase_anon_key: string;
  salon_name?: string;
  supabase_service_role_key?: string;
  database_password?: string;
  is_enterprise?: boolean;
}

export function getIsEnterpriseSession(): boolean {
  return getTenantConfigFromSession()?.is_enterprise === true;
}

/**
 * Store tenant_id and config in localStorage (client-side)
 * @param tenantId - Tenant UUID
 * @param tenantConfig - Tenant configuration (supabase_url and anon_key)
 */
export function storeTenantIdInSession(
  tenantId: string,
  tenantConfig?: StoredTenantConfig,
): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(TENANT_ID_KEY, tenantId);
    if (tenantConfig) {
      localStorage.setItem(
        TENANT_CONFIG_KEY,
        JSON.stringify(tenantConfig),
      );
      const displayName = tenantConfig.salon_name?.trim();
      if (displayName) {
        writeSalonDisplayCache(displayName);
      }
    }
  }
}

/**
 * Get tenant_id from localStorage (client-side)
 * @returns tenant_id if found, null otherwise
 */
export function getTenantIdFromSession(): string | null {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(TENANT_ID_KEY);
  }
  return null;
}

/**
 * Get tenant config from localStorage (client-side)
 * @returns tenant config if found, null otherwise
 */
export function getTenantConfigFromSession(): StoredTenantConfig | null {
  if (typeof window !== 'undefined') {
    const configStr = localStorage.getItem(TENANT_CONFIG_KEY);
    if (configStr) {
      try {
        return JSON.parse(configStr) as StoredTenantConfig;
      } catch {
        return null;
      }
    }
  }
  return null;
}

/**
 * Get salon_name from stored tenant config (client-side)
 * @returns salon_name if found, null otherwise
 */
export function getSalonNameFromSession(): string | null {
  return getTenantConfigFromSession()?.salon_name ?? null;
}

/**
 * Salon name for UI: dedicated key first (always set on login when known), then tenant_config.
 * Backfills the dedicated key from tenant_config when missing (older sessions).
 */
export function getPersistedSalonDisplayName(): string | null {
  if (typeof window === 'undefined') return null;
  const cached = localStorage.getItem(SALON_DISPLAY_NAME_KEY)?.trim();
  if (cached) return cached;
  const fromConfig = getSalonNameFromSession()?.trim() || null;
  if (fromConfig) {
    // getSnapshot for useSyncExternalStore (via salonLabelClientSnapshot) must
    // stay side-effect-free: cache the backfill but don't notify subscribers,
    // or it re-enters setState on the component currently rendering this value.
    setSalonDisplayCache(fromConfig);
  }
  return fromConfig;
}

/** Subscribe to localStorage-backed salon label (same tab + other tabs). */
export function subscribeTenantSalonDisplay(onStoreChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (e: StorageEvent) => {
    if (
      e.key === SALON_DISPLAY_NAME_KEY ||
      e.key === TENANT_CONFIG_KEY ||
      e.key === TENANT_ID_KEY ||
      e.key === null
    ) {
      onStoreChange();
    }
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(TENANT_SALON_DISPLAY_CHANGE_EVENT, onStoreChange);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(TENANT_SALON_DISPLAY_CHANGE_EVENT, onStoreChange);
  };
}

/** Merge salon_name into stored tenant_config so UI can read it before async fetches finish. */
export function mergeSalonNameIntoSession(salonName: string): void {
  if (typeof window === 'undefined') return;
  const tenantId = getTenantIdFromSession();
  const config = getTenantConfigFromSession();
  if (!tenantId || !config || !salonName) return;
  const trimmed = salonName.trim();
  if (config.salon_name === trimmed) {
    if (!localStorage.getItem(SALON_DISPLAY_NAME_KEY)?.trim()) {
      writeSalonDisplayCache(trimmed);
    }
    return;
  }
  storeTenantIdInSession(tenantId, { ...config, salon_name: trimmed });
}

/**
 * Remove tenant_id and config from session (on logout)
 */
export function clearTenantIdFromSession(): void {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(TENANT_ID_KEY);
    localStorage.removeItem(TENANT_CONFIG_KEY);
    localStorage.removeItem(SALON_DISPLAY_NAME_KEY);
    localStorage.removeItem(SALON_DISPLAY_UPDATED_AT_KEY);
    notifySalonDisplaySubscribers();
  }
}

/** UI prefs that are not tied to a session and should survive a login wipe. */
const PRESERVED_LOCAL_KEYS = ['theme'];

/**
 * Wipe all session-scoped browser state (localStorage, sessionStorage, JS-readable cookies).
 * Call at every login before establishing the new session so stale tenant config, auth tokens,
 * filters, carts, and UI cookies from a previous session never leak across logins.
 *
 * The httpOnly `admin_session` cookie is set by the server and is not JS-readable, so the cookie
 * sweep below does not remove it — a fresh admin login is unaffected.
 */
export function clearAllClientState(): void {
  if (typeof window === 'undefined') return;

  const preserved = new Map<string, string>();
  for (const key of PRESERVED_LOCAL_KEYS) {
    const v = localStorage.getItem(key);
    if (v !== null) preserved.set(key, v);
  }

  localStorage.clear();
  sessionStorage.clear();
  for (const [key, v] of preserved) localStorage.setItem(key, v);

  const hostname = window.location.hostname;
  for (const raw of document.cookie.split(';')) {
    const name = raw.split('=')[0]?.trim();
    if (!name) continue;
    for (const path of ['/', '/auth', '/api']) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${hostname}`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=.${hostname}`;
    }
  }

  notifySalonDisplaySubscribers();
}

/**
 * Store tenant_id in cookie (server-side)
 * This is used for server components and API routes
 */
export function setTenantIdCookie(tenantId: string): void {
  if (typeof document !== 'undefined') {
    // Client-side cookie setting (for SSR)
    document.cookie = `${TENANT_ID_KEY}=${tenantId}; path=/; max-age=31536000; SameSite=Lax`;
  }
  
}
export function isDemoAccount(): boolean {
  if (typeof document !== 'undefined') {
    return document.cookie
      .split('; ')
      .some((c) => c.trim().startsWith('is_demo=true'));
  }
  return false;
}
